import {
  boardResponseSchema,
  type CreateTaskRequest,
  deletionResponseSchema,
  taskDetailSchema,
  taskSchema,
  type UpdateTaskRequest,
} from '@wednesday/contracts';
import { queryOptions } from '@tanstack/react-query';

import { projectKeys } from '@/features/projects/api';
import { apiRequest } from '@/shared/api/client';

/** Server calls of the board (api-design §3.5). */

/** Under `projects` so that every project invalidation also refreshes the board. */
export const boardKey = (projectId: string) => [...projectKeys.all, 'board', projectId] as const;

export const boardQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: boardKey(projectId),
    queryFn: ({ signal }) =>
      apiRequest('GET', `/projects/${projectId}/board`, { schema: boardResponseSchema, signal }),
  });

export function createTask(body: CreateTaskRequest) {
  return apiRequest('POST', '/tasks', { body, schema: taskSchema });
}

export function updateTask(taskId: string, body: UpdateTaskRequest) {
  return apiRequest('PATCH', `/tasks/${taskId}`, { body, schema: taskSchema });
}

export function replaceOwners(taskId: string, ownerIds: readonly string[]) {
  return apiRequest('PUT', `/tasks/${taskId}/owners`, { body: { ownerIds }, schema: taskSchema });
}

export function moveTask(taskId: string, body: { groupId?: string; afterId: string | null }) {
  return apiRequest('POST', `/tasks/${taskId}/move`, { body, schema: taskSchema });
}

export function deleteTask(taskId: string) {
  return apiRequest('DELETE', `/tasks/${taskId}`, { schema: deletionResponseSchema });
}

/** A task by ID or key such as `WED-123` (CRD-1, TSK-10). */
export const taskQueryOptions = (idOrKey: string) =>
  queryOptions({
    queryKey: [...projectKeys.all, 'task', idOrKey] as const,
    queryFn: ({ signal }) =>
      apiRequest('GET', `/tasks/${encodeURIComponent(idOrKey)}`, {
        schema: taskDetailSchema,
        signal,
      }),
  });
